import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import LeadCaptureModal from "@/components/LeadCaptureModal";

const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Testimonials", path: "/testimonials" },
  { label: "Blog", path: "/blog" },
  { label: "News", path: "/news" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-gray-200/60 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
              <div className="w-9 h-9 rounded-xl bg-[#0B1F3A] flex items-center justify-center">
                <span className="text-white font-bold text-sm font-['Outfit']">MF</span>
              </div>
              <div className="hidden sm:block">
                <span className="font-['Outfit'] font-semibold text-[#0B1F3A] text-lg leading-tight">
                  Micro Financial Services
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-[#3B82F6] ${
                    location.pathname === item.path
                      ? "text-[#3B82F6]"
                      : "text-[#111111]"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+917506020708"
                className="flex items-center gap-2 text-sm font-medium text-[#0B1F3A] hover:text-[#3B82F6] transition-colors"
                data-testid="header-call-btn"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden xl:inline">+91 7506020708</span>
              </a>
              <button
                onClick={() => setShowLeadModal(true)}
                className="bg-[#0B1F3A] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#111827] transition-all shadow-md hover:shadow-lg btn-glow"
                data-testid="header-cta-btn"
              >
                Get Free Plan
              </button>
            </div>

            {/* Mobile menu toggle */}
            <button
              className="lg:hidden p-2 text-[#0B1F3A]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-lg"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block py-3 text-base font-medium transition-colors ${
                    location.pathname === item.path
                      ? "text-[#3B82F6]"
                      : "text-[#111111]"
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-gray-100 space-y-3">
                <a
                  href="tel:+917506020708"
                  className="flex items-center gap-2 py-2 text-sm font-medium text-[#0B1F3A]"
                >
                  <Phone className="w-4 h-4" /> +91 7506020708
                </a>
                <button
                  onClick={() => { setShowLeadModal(true); setMobileOpen(false); }}
                  className="w-full bg-[#0B1F3A] text-white text-sm font-semibold px-5 py-3 rounded-full"
                  data-testid="mobile-cta-btn"
                >
                  Get Free Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <LeadCaptureModal open={showLeadModal} onOpenChange={setShowLeadModal} />
    </>
  );
}
